import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingStatus } from "../sessions/models/LoadingStatus";
import LoadingSpinner from "../../components/LoadingSpinner";
import DurationSelector from "../../components/duration-selector/DurationSelector";
import TimePicker from "../../components/time-picker/TimePicker";
import SegmentedToggle from "../../components/SegmentedToggle";
import {
  backfillCalendarInvites,
  fetchSchedulePreferences,
  saveSchedulePreferences,
} from "./slice-extensions/instructor-preferences-slice-extension";
import {
  selectInstructorPreferences,
  selectPreferencesInitializationStatus,
  selectPreferencesSaveStatus,
} from "./instructor-preferences-slice";
import { WorkingHoursMode } from "./contracts/WorkingHoursMode";
import { DayOfWeek } from "./contracts/DayOfWeek";
import { ALL_DAYS, hhmmToMinutes } from "./utils/workingHoursUtils";
import { DEFAULT_CURRENCY } from "./utils/currencyOptions";
import BillingCurrencySelector from "./BillingCurrencySelector";
import WeekStartSelector from "./WeekStartSelector";
import { SchedulePreferences } from "./models/SchedulePreferences";
import { WorkingHoursWindow } from "./models/WorkingHoursWindow";
import {
  showSuccessNotification,
  showErrorNotification,
} from "../../utils/notifications";

const DEFAULT_START = "09:00";
const DEFAULT_END = "17:00";
const DAY_LABEL_KEY: Record<DayOfWeek, string> = {
  [DayOfWeek.Monday]: "schedulePreferences.monday",
  [DayOfWeek.Tuesday]: "schedulePreferences.tuesday",
  [DayOfWeek.Wednesday]: "schedulePreferences.wednesday",
  [DayOfWeek.Thursday]: "schedulePreferences.thursday",
  [DayOfWeek.Friday]: "schedulePreferences.friday",
  [DayOfWeek.Saturday]: "schedulePreferences.saturday",
  [DayOfWeek.Sunday]: "schedulePreferences.sunday",
};

interface DayRow {
  day: DayOfWeek;
  enabled: boolean;
  start: string;
  end: string;
}

interface Props {
  hideHeader?: boolean;
}

const SchedulePreferencesSection = ({ hideHeader = false }: Props = {}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const preferences = useAppSelector(selectInstructorPreferences);
  const initializationStatus = useAppSelector(selectPreferencesInitializationStatus);
  const saveStatus = useAppSelector(selectPreferencesSaveStatus);

  const [mode, setMode] = useState<WorkingHoursMode>(WorkingHoursMode.SameEveryDay);
  const [singleStart, setSingleStart] = useState(DEFAULT_START);
  const [singleEnd, setSingleEnd] = useState(DEFAULT_END);
  const [dayRows, setDayRows] = useState<DayRow[]>(
    ALL_DAYS.map((d) => ({ day: d, enabled: true, start: DEFAULT_START, end: DEFAULT_END }))
  );
  const [defaultDuration, setDefaultDuration] = useState<string>("60");
  const [currency, setCurrency] = useState<string>(DEFAULT_CURRENCY);
  const [calendarInvites, setCalendarInvites] = useState(false);
  const [memberScheduleEmails, setMemberScheduleEmails] = useState(false);
  const [weekStart, setWeekStart] = useState<DayOfWeek>(DayOfWeek.Monday);
  const [error, setError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (initializationStatus === "not-initialized") {
      dispatch(fetchSchedulePreferences());
    }
  }, [dispatch, initializationStatus]);

  // Hydrate local form state when preferences arrive.
  useEffect(() => {
    if (!preferences) return;
    setMode(preferences.workingHoursMode);
    setDefaultDuration(String(preferences.defaultDurationMinutes));
    setCurrency(preferences.currency || DEFAULT_CURRENCY);
    setCalendarInvites(preferences.calendarInvitesEnabled);
    setMemberScheduleEmails(preferences.memberScheduleEmailsEnabled);
    setWeekStart(preferences.weekStart);

    if (preferences.workingHoursMode === WorkingHoursMode.SameEveryDay) {
      const single = preferences.workingHours.find((w) => w.dayOfWeek === null);
      setSingleStart(single?.start || DEFAULT_START);
      setSingleEnd(single?.end || DEFAULT_END);
    } else {
      // PerDay: omitted days come back disabled.
      const lookup = new Map<DayOfWeek, WorkingHoursWindow>();
      preferences.workingHours.forEach((w) => {
        if (w.dayOfWeek !== null) lookup.set(w.dayOfWeek, w);
      });
      setDayRows(
        ALL_DAYS.map((d) => {
          const w = lookup.get(d);
          return w
            ? { day: d, enabled: true, start: w.start, end: w.end }
            : { day: d, enabled: false, start: DEFAULT_START, end: DEFAULT_END };
        })
      );
    }
  }, [preferences]);

  const buildPayload = (): SchedulePreferences | undefined => {
    setError(undefined);

    const durationNum = parseInt(defaultDuration, 10);
    if (!durationNum || durationNum < 5 || durationNum > 1440) {
      setError(t("schedulePreferences.errorDurationRange"));
      return undefined;
    }

    if (mode === WorkingHoursMode.SameEveryDay) {
      if (hhmmToMinutes(singleEnd) <= hhmmToMinutes(singleStart)) {
        setError(t("schedulePreferences.errorEndAfterStart"));
        return undefined;
      }
      return {
        workingHoursMode: WorkingHoursMode.SameEveryDay,
        workingHours: [{ dayOfWeek: null, start: singleStart, end: singleEnd }],
        defaultDurationMinutes: durationNum,
        currency,
        calendarInvitesEnabled: calendarInvites,
        memberScheduleEmailsEnabled: memberScheduleEmails,
        weekStart,
      };
    }

    const enabled = dayRows.filter((r) => r.enabled);
    if (enabled.length === 0) {
      setError(t("schedulePreferences.errorAtLeastOneDay"));
      return undefined;
    }
    for (const row of enabled) {
      if (hhmmToMinutes(row.end) <= hhmmToMinutes(row.start)) {
        setError(t("schedulePreferences.errorDayEndAfterStart", { day: t(DAY_LABEL_KEY[row.day]) }));
        return undefined;
      }
    }
    return {
      workingHoursMode: WorkingHoursMode.PerDay,
      workingHours: enabled.map((r) => ({ dayOfWeek: r.day, start: r.start, end: r.end })),
      defaultDurationMinutes: durationNum,
      currency,
      calendarInvitesEnabled: calendarInvites,
      memberScheduleEmailsEnabled: memberScheduleEmails,
      weekStart,
    };
  };

  const handleSave = async () => {
    const payload = buildPayload();
    if (!payload) return;
    const invitesJustEnabled =
      payload.calendarInvitesEnabled && !preferences?.calendarInvitesEnabled;
    const result = await dispatch(saveSchedulePreferences(payload));
    if (saveSchedulePreferences.fulfilled.match(result)) {
      showSuccessNotification(t("schedulePreferences.saved"));
      if (invitesJustEnabled) {
        // Catch the calendar up with the existing schedule.
        const backfill = await dispatch(backfillCalendarInvites());
        if (backfillCalendarInvites.fulfilled.match(backfill)) {
          showSuccessNotification(
            backfill.payload > 0
              ? t("schedulePreferences.sessionsSent", { count: backfill.payload })
              : t("schedulePreferences.invitesEnabled")
          );
        }
      }
    } else {
      showErrorNotification(t("schedulePreferences.saveFailed"));
    }
  };

  const updateDayRow = (day: DayOfWeek, patch: Partial<DayRow>) => {
    setDayRows((prev) => prev.map((r) => (r.day === day ? { ...r, ...patch } : r)));
  };

  const isLoading = initializationStatus === "initializing";
  const isSaving = saveStatus === LoadingStatus.Loading;
  const dayRowsByDay = useMemo(() => {
    const m = new Map<DayOfWeek, DayRow>();
    dayRows.forEach((r) => m.set(r.day, r));
    return m;
  }, [dayRows]);

  return (
    <div className="space-y-5">
      {!hideHeader && (
        <>
          <h2 className="text-lg font-semibold">{t("settings.workingHoursTitle")}</h2>
          <p className="text-sm text-grey-50 dark:text-grey-70">
            {t("settings.workingHoursDescription")}
          </p>
        </>
      )}

      {isLoading && (
        <div className="py-4">
          <LoadingSpinner text={t("schedulePreferences.loading")} />
        </div>
      )}

      {!isLoading && (
        <>
          {/* Mode toggle (segmented control) */}
          <div className="space-y-2">
            <p className="text-sm font-semibold text-grey-30 dark:text-grey-90">
              {t("schedulePreferences.schedule")}
            </p>
            <SegmentedToggle
              value={mode}
              onChange={(v) => setMode(v)}
              options={[
                { value: WorkingHoursMode.SameEveryDay, label: t("schedulePreferences.sameEveryDay") },
                { value: WorkingHoursMode.PerDay, label: t("schedulePreferences.differentPerDay") },
              ]}
            />
          </div>

          {/* Single-window mode */}
          {mode === WorkingHoursMode.SameEveryDay && (
            <div className="flex items-center gap-3">
              <TimePicker label="" value={singleStart} onChange={setSingleStart} />
              <span className="text-grey-50 dark:text-grey-70 text-sm">{t("schedulePreferences.to")}</span>
              <TimePicker label="" value={singleEnd} onChange={setSingleEnd} />
            </div>
          )}

          {/* Per-day mode — two-column layout (Mon-Thu | Fri-Sun) on lg, single column otherwise */}
          {mode === WorkingHoursMode.PerDay && (() => {
            const renderRow = (day: DayOfWeek) => {
              const row = dayRowsByDay.get(day)!;
              return (
                <div
                  key={day}
                  className="grid grid-cols-[120px_auto_auto_auto_auto] items-center gap-3"
                >
                  <label className="flex cursor-pointer items-center gap-2">
                    <input
                      type="checkbox"
                      checked={row.enabled}
                      onChange={(e) => updateDayRow(day, { enabled: e.target.checked })}
                    />
                    <span className="font-medium">{t(DAY_LABEL_KEY[day])}</span>
                  </label>
                  <div className={row.enabled ? "" : "opacity-40 pointer-events-none"}>
                    <TimePicker
                      label=""
                      value={row.start}
                      onChange={(v) => updateDayRow(day, { start: v })}
                      disabled={!row.enabled}
                    />
                  </div>
                  <span className="text-grey-50 dark:text-grey-70 text-sm">{t("schedulePreferences.to")}</span>
                  <div className={row.enabled ? "" : "opacity-40 pointer-events-none"}>
                    <TimePicker
                      label=""
                      value={row.end}
                      onChange={(v) => updateDayRow(day, { end: v })}
                      disabled={!row.enabled}
                    />
                  </div>
                  {!row.enabled && (
                    <span className="text-sm text-grey-50 dark:text-grey-70">{t("schedulePreferences.off")}</span>
                  )}
                </div>
              );
            };

            const firstColumn = ALL_DAYS.slice(0, 4);   // Mon-Thu
            const secondColumn = ALL_DAYS.slice(4);      // Fri-Sun

            return (
              <div className="grid grid-cols-1 gap-x-10 gap-y-2 lg:grid-cols-2">
                <div className="space-y-2">{firstColumn.map(renderRow)}</div>
                <div className="space-y-2">{secondColumn.map(renderRow)}</div>
              </div>
            );
          })()}

          {/* Default duration — separated by a divider */}
          <div className="border-t border-grey-80 dark:border-grey-50 pt-5">
            <DurationSelector
              selectedDuration={defaultDuration}
              onDurationSelect={setDefaultDuration}
            />
          </div>

          {/* Week start — how the calendar lays out the week */}
          <WeekStartSelector value={weekStart} onChange={setWeekStart} />

          {/* Billing currency — applies to every member of this instructor */}
          <BillingCurrencySelector value={currency} onChange={setCurrency} />

          {/* Calendar invites — sessions land in the instructor's own calendar */}
          <div className="border-t border-grey-80 dark:border-grey-50 pt-5">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 cursor-pointer"
                checked={calendarInvites}
                onChange={(e) => setCalendarInvites(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-semibold text-grey-30 dark:text-grey-90">
                  {t("schedulePreferences.calendarInvitesTitle")}
                </span>
                <span className="block text-sm text-grey-50 dark:text-grey-70">
                  {t("schedulePreferences.calendarInvitesDescription")}
                </span>
              </span>
            </label>
          </div>

          {/* Member schedule emails — members hear about cancellations and time changes */}
          <div className="border-t border-grey-80 dark:border-grey-50 pt-5">
            <label className="flex cursor-pointer items-start gap-3">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 shrink-0 cursor-pointer"
                checked={memberScheduleEmails}
                onChange={(e) => setMemberScheduleEmails(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-semibold text-grey-30 dark:text-grey-90">
                  {t("schedulePreferences.memberEmailsTitle")}
                </span>
                <span className="block text-sm text-grey-50 dark:text-grey-70">
                  {t("schedulePreferences.memberEmailsDescription")}
                </span>
              </span>
            </label>
          </div>

          {error && <p className="field-error">{error}</p>}

          {/* Save — right-aligned at the bottom */}
          <div className="flex justify-end pt-2">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSave}
              disabled={isSaving}
            >
              {isSaving ? t("schedulePreferences.saving") : t("schedulePreferences.save")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default SchedulePreferencesSection;
